import React from 'react'
import {
  Box,
  Button,
  Card,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Add, ArrowDownward, ArrowUpward, Delete } from '@mui/icons-material'
import {
  CondicaoFormulario,
  EtapaFormulario,
  ItemFormulario,
  MARCO_EXIGENCIA_LABEL,
  MarcoExigencia,
  OPERADOR_CONDICAO_LABEL,
  OperadorCondicao,
  TIPO_CAMPO_LABEL,
  TipoCampoFormulario,
  ValidacaoSomaFormulario,
} from '@/types/formularioAdmin'
import {
  camposReferenciaveis,
  chaveDoRotulo,
  ehNumerico,
  novoItem,
} from '@/utils/formularioSchemaAdmin'

/**
 * Um campo do formulário do produto no editor do ADMIN (AN-6 §6): rótulo, chave, tipo,
 * opções, "Obrigatório em", condição de exibição, obrigatoriedade condicional, "Enviar à
 * IA" e — para GRUPO — repetição, quantidade, mínimo, máximo, subcampos e soma.
 */

/** Na Estruturação a reserva já foi enviada: o marco ENVIO_ANALISE não existe mais (§1.1). */
export const marcosDaEtapa = (etapa: EtapaFormulario): MarcoExigencia[] =>
  etapa === 'ESTRUTURACAO' ? ['ENCAMINHAMENTO_PARCEIRO'] : ['ENVIO_ANALISE', 'ENCAMINHAMENTO_PARCEIRO']

const TIPOS = Object.keys(TIPO_CAMPO_LABEL) as TipoCampoFormulario[]
const OPERADORES = Object.keys(OPERADOR_CONDICAO_LABEL) as OperadorCondicao[]

const numeroOuNada = (v: string): number | undefined => (v.trim() === '' ? undefined : Number(v))

interface CondicaoProps {
  titulo: string
  condicao: CondicaoFormulario | null | undefined
  referenciaveis: ItemFormulario[]
  somenteLeitura: boolean
  onChange: (c: CondicaoFormulario | undefined) => void
}

const CondicaoEditor: React.FC<CondicaoProps> = ({ titulo, condicao, referenciaveis, somenteLeitura, onChange }) => {
  const ativa = !!condicao
  const alvo = referenciaveis.find((r) => r.chave === condicao?.campo)
  const set = (m: Partial<CondicaoFormulario>) =>
    onChange({ campo: '', operador: 'IGUAL', ...condicao, ...m })

  /**
   * Troca de operador: UM_DE guarda `valores: string[]` (canônico do backend); os demais,
   * `valor`. O que já estava escrito migra de um formato para o outro.
   */
  const trocarOperador = (operador: OperadorCondicao) => {
    if (!condicao) return
    if (operador === 'UM_DE') {
      const atual = condicao.valores?.length ? condicao.valores : condicao.valor ? [condicao.valor] : []
      onChange({ campo: condicao.campo, operador, valores: atual })
    } else if (operador === 'PREENCHIDO') {
      onChange({ campo: condicao.campo, operador })
    } else {
      onChange({ campo: condicao.campo, operador, valor: condicao.valor ?? condicao.valores?.[0] ?? '' })
    }
  }

  const campoValor = () => {
    if (!condicao || condicao.operador === 'PREENCHIDO') return null
    if (condicao.operador === 'UM_DE') {
      const valores = condicao.valores ?? []
      const comumLista = {
        disabled: somenteLeitura,
        size: 'small' as const,
        fullWidth: true,
        inputProps: { 'aria-label': `${titulo}: valores` },
      }
      if (alvo?.tipo === 'OPCAO' && (alvo.opcoes ?? []).length > 0) {
        return (
          <TextField
            select
            label="Valores"
            value={valores}
            SelectProps={{ multiple: true }}
            onChange={(e) => {
              const v = e.target.value as unknown
              set({ valores: Array.isArray(v) ? (v as string[]) : String(v).split(','), valor: undefined })
            }}
            {...comumLista}
          >
            {(alvo.opcoes ?? []).map((o) => (
              <MenuItem key={o.valor} value={o.valor}>{o.rotulo}</MenuItem>
            ))}
          </TextField>
        )
      }
      // Texto livre: um valor por "|". O split preserva o que está sendo digitado; a limpeza
      // (trim, vazios) é do `normalizarSchema`, no que vai ao backend.
      return (
        <TextField
          label="Valores (separe com |)"
          value={valores.join('|')}
          onChange={(e) => set({ valores: e.target.value.split('|'), valor: undefined })}
          {...comumLista}
        />
      )
    }
    const comum = {
      label: 'Valor',
      value: condicao.valor ?? '',
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => set({ valor: e.target.value }),
      disabled: somenteLeitura,
      size: 'small' as const,
      fullWidth: true,
      inputProps: { 'aria-label': `${titulo}: valor` },
    }
    if (alvo?.tipo === 'SIM_NAO') {
      return (
        <TextField select {...comum}>
          <MenuItem value="true">Sim</MenuItem>
          <MenuItem value="false">Não</MenuItem>
        </TextField>
      )
    }
    if (alvo?.tipo === 'OPCAO' && (alvo.opcoes ?? []).length > 0) {
      return (
        <TextField select {...comum}>
          {(alvo.opcoes ?? []).map((o) => (
            <MenuItem key={o.valor} value={o.valor}>{o.rotulo}</MenuItem>
          ))}
        </TextField>
      )
    }
    return <TextField {...comum} />
  }

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={ativa}
            disabled={somenteLeitura || (!ativa && referenciaveis.length === 0)}
            onChange={(e) =>
              onChange(e.target.checked ? { campo: referenciaveis[0]?.chave ?? '', operador: 'IGUAL', valor: '' } : undefined)
            }
            inputProps={{ 'aria-label': titulo }}
          />
        }
        label={<Typography variant="caption">{titulo}</Typography>}
      />
      {ativa && condicao && (
        <Grid container spacing={1} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Campo"
              value={condicao.campo}
              onChange={(e) =>
                set(
                  condicao.operador === 'UM_DE'
                    ? { campo: e.target.value, valores: [] }
                    : { campo: e.target.value, valor: '' }
                )
              }
              disabled={somenteLeitura}
              size="small"
              fullWidth
              inputProps={{ 'aria-label': `${titulo}: campo` }}
            >
              {referenciaveis.map((r) => (
                <MenuItem key={r.chave} value={r.chave}>{r.rotulo || r.chave}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Operador"
              value={condicao.operador}
              onChange={(e) => trocarOperador(e.target.value as OperadorCondicao)}
              disabled={somenteLeitura}
              size="small"
              fullWidth
            >
              {OPERADORES.map((o) => (
                <MenuItem key={o} value={o}>{OPERADOR_CONDICAO_LABEL[o]}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>{campoValor()}</Grid>
        </Grid>
      )}
    </Box>
  )
}

interface Props {
  item: ItemFormulario
  /** Itens do topo do schema — para saber o que vem antes (condições e quantidade). */
  itensTopo: ItemFormulario[]
  indiceTopo: number
  /** Presente quando o item é subcampo de um grupo. */
  indiceNoGrupo?: number
  total: number
  etapa: EtapaFormulario
  somenteLeitura: boolean
  onChange: (item: ItemFormulario) => void
  onRemover: () => void
  onMover: (delta: -1 | 1) => void
}

const ItemFormularioEditor: React.FC<Props> = ({
  item,
  itensTopo,
  indiceTopo,
  indiceNoGrupo,
  total,
  etapa,
  somenteLeitura,
  onChange,
  onRemover,
  onMover,
}) => {
  const dentroDeGrupo = indiceNoGrupo !== undefined
  const posicao = dentroDeGrupo ? indiceNoGrupo : indiceTopo
  const nome = item.rotulo || `campo ${posicao + 1}`
  const set = (m: Partial<ItemFormulario>) => onChange({ ...item, ...m })
  const referenciaveis = camposReferenciaveis(itensTopo, indiceTopo, indiceNoGrupo)
  const tipos = dentroDeGrupo ? TIPOS.filter((t) => t !== 'GRUPO') : TIPOS

  const alterarRotulo = (rotulo: string) => {
    // A chave acompanha o rótulo até alguém editá-la à mão.
    const segue = item.chave === '' || item.chave === chaveDoRotulo(item.rotulo)
    set({ rotulo, ...(segue ? { chave: chaveDoRotulo(rotulo) } : {}) })
  }

  const alterarTipo = (tipo: TipoCampoFormulario) => {
    const m: Partial<ItemFormulario> = { tipo }
    if (tipo === 'GRUPO' && !item.campos) Object.assign(m, { repeatableGroup: true, campos: [] })
    if (tipo === 'OPCAO' && !item.opcoes) m.opcoes = [{ valor: '', rotulo: '' }]
    if (tipo === 'CPF' || tipo === 'GRUPO') m.enviarParaIa = false
    set(m)
  }

  const campos = item.campos ?? []
  const numerosAnteriores = itensTopo.slice(0, indiceTopo).filter((i) => i.tipo === 'NUMERO')
  const soma: ValidacaoSomaFormulario | undefined = item.validacoes?.find((v) => v.tipo === 'SOMA')
  const camposNumericos = campos.filter((c) => ehNumerico(c.tipo))
  const marcos = marcosDaEtapa(etapa)

  return (
    <Card variant="outlined" sx={{ p: 2, bgcolor: dentroDeGrupo ? 'grey.50' : undefined }} aria-label={`Campo: ${nome}`} component="section">
      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField
            label="Rótulo"
            value={item.rotulo}
            onChange={(e) => alterarRotulo(e.target.value)}
            disabled={somenteLeitura}
            size="small"
            fullWidth
            inputProps={{ 'aria-label': `Rótulo do ${nome}` }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Chave"
            value={item.chave}
            onChange={(e) => set({ chave: chaveDoRotulo(e.target.value) })}
            disabled={somenteLeitura}
            size="small"
            fullWidth
            helperText="Identidade do campo nas respostas"
          />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            select
            label="Tipo"
            value={item.tipo}
            onChange={(e) => alterarTipo(e.target.value as TipoCampoFormulario)}
            disabled={somenteLeitura}
            size="small"
            fullWidth
          >
            {tipos.map((t) => (
              <MenuItem key={t} value={t}>{TIPO_CAMPO_LABEL[t]}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            select
            label="Obrigatório em"
            value={item.obrigatorioEm ?? ''}
            onChange={(e) => set({ obrigatorioEm: (e.target.value || undefined) as MarcoExigencia | undefined })}
            disabled={somenteLeitura}
            size="small"
            fullWidth
          >
            <MenuItem value="">Opcional</MenuItem>
            {/* Marco fora da etapa (ex.: schema copiado do Cadastro) aparece para ser trocado. */}
            {[...marcos, ...(item.obrigatorioEm && !marcos.includes(item.obrigatorioEm) ? [item.obrigatorioEm] : [])].map((m) => (
              <MenuItem key={m} value={m} disabled={!marcos.includes(m)}>
                {MARCO_EXIGENCIA_LABEL[m]}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={1}>
          {!somenteLeitura && (
            <Stack direction="row" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Tooltip title="Subir">
                <span>
                  <IconButton size="small" aria-label={`Subir ${nome}`} disabled={posicao === 0} onClick={() => onMover(-1)}>
                    <ArrowUpward fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Descer">
                <span>
                  <IconButton size="small" aria-label={`Descer ${nome}`} disabled={posicao === total - 1} onClick={() => onMover(1)}>
                    <ArrowDownward fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remover">
                <IconButton size="small" aria-label={`Remover ${nome}`} onClick={onRemover}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Grid>
      </Grid>

      {item.tipo === 'OPCAO' && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">Opções</Typography>
          <Stack spacing={1} sx={{ mt: 0.5 }}>
            {(item.opcoes ?? []).map((o, k) => (
              <Stack key={k} direction="row" spacing={1} alignItems="center">
                <TextField
                  label="Rótulo da opção"
                  value={o.rotulo}
                  onChange={(e) => {
                    const opcoes = [...(item.opcoes ?? [])]
                    const segue = o.valor === '' || o.valor === chaveDoRotulo(o.rotulo).toUpperCase()
                    opcoes[k] = { rotulo: e.target.value, valor: segue ? chaveDoRotulo(e.target.value).toUpperCase() : o.valor }
                    set({ opcoes })
                  }}
                  disabled={somenteLeitura}
                  size="small"
                />
                <TextField
                  label="Valor"
                  value={o.valor}
                  onChange={(e) => {
                    const opcoes = [...(item.opcoes ?? [])]
                    opcoes[k] = { ...o, valor: e.target.value }
                    set({ opcoes })
                  }}
                  disabled={somenteLeitura}
                  size="small"
                />
                {!somenteLeitura && (
                  <IconButton
                    size="small"
                    aria-label={`Remover opção ${o.rotulo || k + 1}`}
                    onClick={() => set({ opcoes: (item.opcoes ?? []).filter((_, x) => x !== k) })}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ))}
            {!somenteLeitura && (
              <Box>
                <Button size="small" startIcon={<Add />} onClick={() => set({ opcoes: [...(item.opcoes ?? []), { valor: '', rotulo: '' }] })}>
                  Opção
                </Button>
              </Box>
            )}
          </Stack>
        </Box>
      )}

      {ehNumerico(item.tipo) && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <TextField
            label="Mínimo"
            type="number"
            value={item.min ?? ''}
            onChange={(e) => set({ min: numeroOuNada(e.target.value) })}
            disabled={somenteLeitura}
            size="small"
          />
          <TextField
            label="Máximo"
            type="number"
            value={item.max ?? ''}
            onChange={(e) => set({ max: numeroOuNada(e.target.value) })}
            disabled={somenteLeitura}
            size="small"
          />
        </Stack>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 1 }}>
        <Box sx={{ flex: 1 }}>
          <CondicaoEditor
            titulo={`Exibir só quando (${nome})`}
            condicao={item.condition}
            referenciaveis={referenciaveis}
            somenteLeitura={somenteLeitura}
            onChange={(condition) => set({ condition })}
          />
        </Box>
        {item.tipo !== 'GRUPO' && (
          <Box sx={{ flex: 1 }}>
            <CondicaoEditor
              titulo={`Obrigatório só quando (${nome})`}
              condicao={item.requiredWhen}
              referenciaveis={referenciaveis}
              somenteLeitura={somenteLeitura}
              onChange={(requiredWhen) => set({ requiredWhen })}
            />
          </Box>
        )}
        {item.tipo !== 'GRUPO' && (
          <Tooltip title={item.tipo === 'CPF' ? 'CPF nunca vai para a IA' : 'O valor entra no contexto da Defesa com IA'}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!item.enviarParaIa && item.tipo !== 'CPF'}
                  onChange={(e) => set({ enviarParaIa: e.target.checked })}
                  disabled={somenteLeitura || item.tipo === 'CPF'}
                  inputProps={{ 'aria-label': `Enviar à IA: ${nome}` }}
                />
              }
              label={<Typography variant="caption">Enviar à IA</Typography>}
            />
          </Tooltip>
        )}
      </Stack>

      {item.tipo === 'GRUPO' && (
        <Box sx={{ mt: 1.5, pl: { xs: 0, md: 2 }, borderLeft: { md: '3px solid' }, borderColor: { md: 'divider' } }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={!!item.repeatableGroup}
                onChange={(e) => set({ repeatableGroup: e.target.checked })}
                disabled={somenteLeitura}
                inputProps={{ 'aria-label': `Grupo repetível: ${nome}` }}
              />
            }
            label={<Typography variant="caption">Repetível (um cartão por item, ex.: um por sócio)</Typography>}
          />
          {item.repeatableGroup && (
            <Grid container spacing={1} sx={{ my: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Quantidade vem de"
                  value={item.quantidadeDe ?? ''}
                  onChange={(e) => set({ quantidadeDe: e.target.value || undefined })}
                  disabled={somenteLeitura}
                  size="small"
                  fullWidth
                  helperText="Campo Número anterior; o nº de cartões segue a resposta"
                >
                  <MenuItem value="">Livre (o consultor adiciona)</MenuItem>
                  {numerosAnteriores.map((n) => (
                    <MenuItem key={n.chave} value={n.chave}>{n.rotulo || n.chave}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Mín. de itens"
                  type="number"
                  value={item.minItems ?? ''}
                  onChange={(e) => set({ minItems: numeroOuNada(e.target.value) })}
                  disabled={somenteLeitura}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Máx. de itens"
                  type="number"
                  value={item.maxItems ?? ''}
                  onChange={(e) => set({ maxItems: numeroOuNada(e.target.value) })}
                  disabled={somenteLeitura}
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          )}

          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1, mb: 1 }}>
            Subcampos
          </Typography>
          <Stack spacing={1}>
            {campos.map((c, j) => (
              <ItemFormularioEditor
                key={j}
                item={c}
                itensTopo={itensTopo}
                indiceTopo={indiceTopo}
                indiceNoGrupo={j}
                total={campos.length}
                etapa={etapa}
                somenteLeitura={somenteLeitura}
                onChange={(novo) => set({ campos: campos.map((x, y) => (y === j ? novo : x)) })}
                onRemover={() => set({ campos: campos.filter((_, y) => y !== j) })}
                onMover={(d) => {
                  const lista = [...campos]
                  const [m] = lista.splice(j, 1)
                  lista.splice(j + d, 0, m)
                  set({ campos: lista })
                }}
              />
            ))}
          </Stack>
          {!somenteLeitura && (
            <Button size="small" startIcon={<Add />} sx={{ mt: 1 }} onClick={() => set({ campos: [...campos, novoItem()] })}>
              Subcampo
            </Button>
          )}

          {item.repeatableGroup && (
            <Box sx={{ mt: 1.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={!!soma}
                    disabled={somenteLeitura || (!soma && camposNumericos.length === 0)}
                    onChange={(e) =>
                      set({
                        validacoes: e.target.checked
                          ? [{ tipo: 'SOMA', campo: camposNumericos[0]?.chave ?? '', valor: 100, tolerancia: 0.01, marco: marcos[0] }]
                          : undefined,
                      })
                    }
                    inputProps={{ 'aria-label': `Exigir soma: ${nome}` }}
                  />
                }
                label={<Typography variant="caption">Exigir soma dos itens (ex.: participações = 100%)</Typography>}
              />
              {soma && (
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      select
                      label="Subcampo somado"
                      value={soma.campo}
                      onChange={(e) => set({ validacoes: [{ ...soma, campo: e.target.value }] })}
                      disabled={somenteLeitura}
                      size="small"
                      fullWidth
                    >
                      {camposNumericos.map((c) => (
                        <MenuItem key={c.chave} value={c.chave}>{c.rotulo || c.chave}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      label="Total"
                      type="number"
                      value={soma.valor}
                      onChange={(e) => set({ validacoes: [{ ...soma, valor: Number(e.target.value) }] })}
                      disabled={somenteLeitura}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      label="Tolerância"
                      type="number"
                      value={soma.tolerancia ?? ''}
                      onChange={(e) => set({ validacoes: [{ ...soma, tolerancia: numeroOuNada(e.target.value) }] })}
                      disabled={somenteLeitura}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      select
                      label="Exigida em"
                      value={soma.marco ?? marcos[0]}
                      onChange={(e) => set({ validacoes: [{ ...soma, marco: e.target.value as MarcoExigencia }] })}
                      disabled={somenteLeitura}
                      size="small"
                      fullWidth
                    >
                      {marcos.map((m) => (
                        <MenuItem key={m} value={m}>{MARCO_EXIGENCIA_LABEL[m]}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Mensagem"
                      value={soma.mensagem ?? ''}
                      onChange={(e) => set({ validacoes: [{ ...soma, mensagem: e.target.value }] })}
                      disabled={somenteLeitura}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </Box>
      )}
    </Card>
  )
}

export default ItemFormularioEditor
